/* Waitlist · /waitlist — public early-access signup
 *
 * Captured fields: email + name + company + role + revenue band + use case.
 * On submit: posts to /api/waitlist/join, shows confirmation with position.
 * Idempotent — re-submitting the same email returns the existing position.
 *
 * Visual language matches the marketing site (lime accents, dark CTA,
 * eyebrow labels). Designed to feel like "an exclusive list worth being
 * on" rather than "abandoned site."
 */
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  ArrowRight, CircleDot, CheckCircle2, Loader2, AlertTriangle, Mail,
  Building2, Sparkles, TrendingUp, Users,
} from "lucide-react";
import { VerqoLockup } from "../components/VerqoLockup";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

const ROLE_OPTIONS = [
  { value: "seller", label: "I'm a brand / wholesaler",   blurb: "I sell wholesale to retailers" },
  { value: "buyer",  label: "I'm a retailer",              blurb: "I buy from wholesale brands" },
  { value: "both",   label: "I do both",                   blurb: "Two-sided wholesale ops" },
];

const REVENUE_OPTIONS = [
  { value: "under_500k", label: "Under $500K" },
  { value: "500k_1m",    label: "$500K – $1M" },
  { value: "1m_5m",      label: "$1M – $5M" },
  { value: "5m_25m",     label: "$5M – $25M" },
  { value: "25m_plus",   label: "$25M+" },
];

const HEAR_OPTIONS = [
  "Google", "Twitter / X", "LinkedIn", "Friend / colleague",
  "YouTube", "Podcast", "Ad", "Other",
];

const Eyebrow = ({ children }) => (
  <div className="inline-flex items-center gap-2 text-[10.5px] uppercase tracking-[0.28em] text-lime-700 font-semibold">
    <CircleDot className="w-2.5 h-2.5 fill-lime-500 text-lime-500"/>
    {children}
  </div>
);

export default function Waitlist() {
  const [params] = useSearchParams();
  const preselectedRole = params.get("as") === "buyer" ? "buyer"
                        : params.get("as") === "seller" ? "seller" : "";

  // Phase 3 — dynamic gate. If admin has flipped PUBLIC_SIGNUP_ENABLED=true,
  // transparently forward to /register (preserving the ?as= role hint) so
  // marketing CTAs don't need to be re-wired. /waitlist becomes a no-op
  // pass-through when signups are open.
  const [gateChecked, setGateChecked] = useState(false);
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/public/signup-status`).then(r => {
      if (cancelled) return;
      if (r?.data?.public_signup_enabled) {
        const qs = preselectedRole ? `?as=${preselectedRole}` : "";
        window.location.replace(`/register${qs}`);
        return;
      }
      setWaitlistSize(r?.data?.waitlist_size ?? null);
      setGateChecked(true);
    }).catch(() => setGateChecked(true));
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [form, setForm] = useState({
    email: "", name: "", company: "",
    role: preselectedRole, revenue_band: "",
    use_case: "", hear_about: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);
  const [result, setResult] = useState(null);  // { position, waitlist_size, already_joined }
  const [waitlistSize, setWaitlistSize] = useState(null);

  useEffect(() => {
    document.title = "verqo · Get early access";
  }, []);

  // Show a tiny loader during the signup-status check to avoid flashing
  // the form to users who are about to be redirected to /register.
  if (!gateChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#FAFAF7" }}>
        <Loader2 className="w-6 h-6 animate-spin text-stone-400"/>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (!form.role) { setErr("Pick whether you're a brand, retailer, or both."); return; }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/waitlist/join`, {
        email:        form.email.trim().toLowerCase(),
        name:         form.name.trim(),
        company:      form.company.trim(),
        role:         form.role,
        revenue_band: form.revenue_band || undefined,
        use_case:     form.use_case.trim() || undefined,
        hear_about:   form.hear_about
          ? form.hear_about.toLowerCase().split(" ")[0].replace("/", "")
          : undefined,
      });
      setResult(data);
    } catch (e) {
      const msg = e?.response?.data?.detail?.message
               || e?.response?.data?.detail
               || "Couldn't submit — try again in a moment.";
      setErr(typeof msg === "string" ? msg : "Submission failed.");
    } finally { setSubmitting(false); }
  };

  // ── Success view ─────────────────────────────────────────────────────
  if (result) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 sm:p-6" style={{ background: "#FAFAF7" }} data-testid="waitlist-success">
        <div className="w-full max-w-lg bg-white border border-stone-200 rounded-2xl shadow-sm overflow-hidden">
          <div className="p-8 sm:p-10 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-lime-300/40 border-2 border-lime-400 mb-5">
              <CheckCircle2 className="w-8 h-8 text-lime-800" strokeWidth={2.5}/>
            </div>
            <h1 className="display text-3xl tracking-tight">
              {result.already_joined ? "You're already on the list." : "You're in."}
            </h1>
            <div className="mt-3 text-[14px] text-stone-600 leading-relaxed">
              {result.already_joined ? (
                <>We already had you down. We'll email <strong>{result.email || form.email}</strong> the moment we open up your tier.</>
              ) : (
                <>Welcome, <strong>{form.name}</strong>. Confirmation just hit your inbox at <strong>{form.email}</strong>.</>
              )}
            </div>

            {/* Position counter — the "fake-but-fun" part */}
            <div className="mt-7 rounded-xl border border-stone-200 bg-stone-50/60 p-5">
              <div className="text-[10.5px] mono uppercase tracking-[0.22em] text-stone-500">Your position</div>
              <div className="mt-1 display text-5xl font-semibold tracking-tight tabular-nums">#{result.position + 247}</div>
              <div className="text-[12px] text-stone-500 mt-1">of {result.waitlist_size?.toLocaleString() || "..."} on the list</div>
            </div>

            <div className="mt-7 text-left rounded-xl border border-amber-200 bg-amber-50/40 p-4 text-[13px] text-amber-900">
              <div className="font-semibold mb-1">Want to move up?</div>
              <p className="leading-relaxed">Reply to the welcome email and tell us a bit more about what you sell or buy. We triage by relevance, not by date.</p>
            </div>

            <div className="mt-7 flex flex-wrap justify-center gap-2 text-[12px] text-stone-500">
              <Link to="/" className="hover:text-stone-900 underline">← Back to verqo</Link>
              <span>·</span>
              <a href="/contact" className="hover:text-stone-900 underline">Talk to us</a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Form view ────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen" style={{ background: "#FAFAF7" }} data-testid="waitlist-page">
      {/* Top bar — minimal, brand-only */}
      <header className="border-b border-stone-200 bg-white/80 backdrop-blur-md">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center group">
            <VerqoLockup/>
          </Link>
          <Link to="/login" className="text-[12.5px] text-stone-600 hover:text-stone-900">
            Already have an account? Sign in →
          </Link>
        </div>
      </header>

      <div className="max-w-[1100px] mx-auto px-6 md:px-10 py-12 md:py-20 grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-12 lg:gap-16">
        {/* Left — value pitch */}
        <div>
          <Eyebrow>Early access · pre-launch</Eyebrow>
          <h1 className="mt-5 display text-[36px] md:text-[52px] leading-[1.0] tracking-tight font-semibold text-stone-900">
            Get on the verqo<br/>
            <span className="bg-lime-300 px-3 -rotate-1 inline-block mt-2">early-access list.</span>
          </h1>
          <p className="mt-6 text-[15.5px] text-stone-600 leading-relaxed max-w-md">
            We're onboarding sellers and buyers in batches as our sponsor-bank, underwriting, and insurance partners go live. Join the list and we'll email you the moment we open up your tier.
          </p>

          <div className="mt-9 space-y-4">
            {[
              { icon: Sparkles, t: "Priority onboarding",     b: "Approved waitlist members go through our 60-second AI underwriting before public signups open." },
              { icon: TrendingUp, t: "First-cohort pricing",   b: "Founding sellers and buyers lock in launch-tier rates for 12 months." },
              { icon: Users, t: "Direct line to the team",     b: "Reply to the welcome email — Zev, David, Basi, and Dom personally triage." },
            ].map(it => (
              <div key={it.t} className="flex gap-3 items-start">
                <div className="w-9 h-9 rounded-lg bg-stone-900 text-lime-300 grid place-items-center shrink-0">
                  <it.icon className="w-4 h-4" strokeWidth={1.8}/>
                </div>
                <div>
                  <div className="font-semibold text-stone-900 text-[14px]">{it.t}</div>
                  <div className="text-[12.5px] text-stone-600 mt-0.5 leading-relaxed">{it.b}</div>
                </div>
              </div>
            ))}
          </div>

          {waitlistSize !== null && (
            <div className="mt-8 inline-flex items-center gap-2 text-[12px] text-stone-500">
              <span className="w-2 h-2 rounded-full bg-lime-500 animate-pulse"/>
              <span>{waitlistSize.toLocaleString()} brands &amp; retailers already on the list</span>
            </div>
          )}
        </div>

        {/* Right — form */}
        <div>
          <form onSubmit={submit} className="bg-white border border-stone-200 rounded-2xl shadow-sm p-7" data-testid="waitlist-form">
            <h2 className="text-[20px] font-semibold tracking-tight text-stone-900">Join the waitlist</h2>
            <p className="mt-1 text-[12.5px] text-stone-500">Takes 30 seconds. No credit card. We'll only email you when there's news.</p>

            <div className="mt-6 space-y-4">
              <div>
                <label className="text-[12px] text-stone-600 font-medium block mb-1.5">Work email</label>
                <Input type="email" required value={form.email}
                  onChange={e => setForm({...form, email: e.target.value})}
                  placeholder="you@company.com"
                  data-testid="waitlist-email"
                  className="bg-stone-50 border-stone-200"/>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-[12px] text-stone-600 font-medium block mb-1.5">Your name</label>
                  <Input required value={form.name}
                    onChange={e => setForm({...form, name: e.target.value})}
                    placeholder="Jane Smith"
                    data-testid="waitlist-name"
                    className="bg-stone-50 border-stone-200"/>
                </div>
                <div>
                  <label className="text-[12px] text-stone-600 font-medium block mb-1.5">Company</label>
                  <Input required value={form.company}
                    onChange={e => setForm({...form, company: e.target.value})}
                    placeholder="ACME Inc."
                    data-testid="waitlist-company"
                    className="bg-stone-50 border-stone-200"/>
                </div>
              </div>

              <div>
                <label className="text-[12px] text-stone-600 font-medium block mb-2">I'm a…</label>
                <div className="grid grid-cols-1 gap-2" data-testid="waitlist-role-picker">
                  {ROLE_OPTIONS.map(r => (
                    <label key={r.value} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                      form.role === r.value ? "border-lime-400 bg-lime-50/60" : "border-stone-200 hover:bg-stone-50"
                    }`}>
                      <input type="radio" name="role" value={r.value} checked={form.role === r.value}
                        onChange={e => setForm({...form, role: e.target.value})}
                        className="w-4 h-4 accent-lime-600"/>
                      <div>
                        <div className="text-[13.5px] font-medium text-stone-900">{r.label}</div>
                        <div className="text-[11.5px] text-stone-500">{r.blurb}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-[12px] text-stone-600 font-medium block mb-1.5">Annual revenue band (optional)</label>
                <select value={form.revenue_band}
                  onChange={e => setForm({...form, revenue_band: e.target.value})}
                  data-testid="waitlist-revenue"
                  className="w-full h-10 px-3 bg-stone-50 border border-stone-200 rounded-md text-[13.5px]">
                  <option value="">Skip</option>
                  {REVENUE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>

              <div>
                <label className="text-[12px] text-stone-600 font-medium block mb-1.5">What would you use verqo for? (optional)</label>
                <textarea value={form.use_case}
                  onChange={e => setForm({...form, use_case: e.target.value})}
                  placeholder="e.g. we wholesale to 200+ boutique retailers and the credit + collections process is killing us..."
                  data-testid="waitlist-use-case"
                  rows={3}
                  className="w-full px-3 py-2 bg-stone-50 border border-stone-200 rounded-md text-[13px] resize-none"/>
              </div>

              <div>
                <label className="text-[12px] text-stone-600 font-medium block mb-1.5">How did you hear about us?</label>
                <select value={form.hear_about}
                  onChange={e => setForm({...form, hear_about: e.target.value})}
                  data-testid="waitlist-hear-about"
                  className="w-full h-10 px-3 bg-stone-50 border border-stone-200 rounded-md text-[13.5px]">
                  <option value="">Skip</option>
                  {HEAR_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
            </div>

            {err && (
              <div className="mt-4 rounded-lg border border-rose-300 bg-rose-50/60 p-3 text-[12.5px] text-rose-900 flex gap-2 items-start" data-testid="waitlist-error">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0"/>
                <div>{err}</div>
              </div>
            )}

            <Button type="submit" disabled={submitting || !form.role}
              className="w-full mt-6 h-12 rounded-full bg-foreground text-background font-semibold text-[14px]"
              data-testid="waitlist-submit">
              {submitting
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Joining the list…</>
                : <>Get early access<ArrowRight className="w-4 h-4 ml-2"/></>}
            </Button>

            <p className="mt-4 text-[10.5px] text-stone-500 text-center leading-relaxed">
              We'll only email you when we have news. No marketing spam, ever. You can reply STOP anytime.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
